/**
 * Standalone Zustand store for the artifact canvas overlay.
 *
 * Sits outside the global combined store because the canvas lifecycle is
 * tightly coupled to the chat panel — components subscribe directly via
 * `useArtifactStore`. Mirrors the studio's UIArtifact contract minus
 * per-kind buffered swap quirks since v1 is markdown-only.
 *
 * @module store/artifact-store
 */

import { create } from "zustand";
import type { ArtifactKind, PendingSlides, UIArtifact } from "@taqat/core/types";

/** Default canvas state — invisible, empty, idle. */
const defaultArtifact: UIArtifact = {
  id: "",
  title: "New Artifact",
  kind: "document",
  content: "",
  pendingContent: null,
  pendingSlides: null,
  previousContent: null,
  isVisible: false,
  status: "idle",
  error: null,
  lastInstruction: null,
  boundingBox: { top: 0, left: 0, width: 400, height: 300 },
};

/** Public store API — actions stay flat for terse `useArtifactStore.use` calls. */
export interface ArtifactStore {
  artifact: UIArtifact;

  /**
   * Full-screen "focus mode" for the chat + canvas view. When true, the app
   * layout hides the left platform sidebar so only the chat panel and the
   * canvas fill the screen. Kept at the store root (not on `artifact`) because
   * it's a layout concern; the app layout and canvas headers both read it.
   * Always reset when the canvas closes so the sidebar can't get stuck hidden.
   */
  focusMode: boolean;

  /** Patch the canvas state — partial updates merge over the previous value. */
  setArtifact: (patch: Partial<UIArtifact>) => void;

  /** Reset the canvas to defaults; used when a chat unmounts. */
  resetArtifact: () => void;

  /** Toggle full-screen focus mode. */
  toggleFocusMode: () => void;

  /** Explicitly set focus mode on/off. */
  setFocusMode: (on: boolean) => void;

  /**
   * Show the canvas at the supplied trigger element's bounding rect so the
   * overlay animates from where the user clicked. Falling back to centered
   * coordinates keeps imperative open paths working when no element exists.
   */
  showArtifact: (boundingBox?: UIArtifact["boundingBox"]) => void;

  /** Hide the canvas without resetting state — re-opening keeps the body intact. */
  hideArtifact: () => void;

  /**
   * Initialize a fresh artifact of the given kind and start streaming.
   * Used by `data-artifact-init` with `mode: "create"` to clear stale content.
   */
  beginCreate: (input: { id: string; title: string; kind: ArtifactKind }) => void;

  /**
   * Begin buffering an update stream. Keeps existing content visible while
   * deltas accumulate in `pendingContent`; the swap happens in `finishStream`.
   */
  beginUpdate: (input: { id: string; title: string; kind: ArtifactKind }) => void;

  /** Append a streaming delta to either `content` (create) or `pendingContent` (update). */
  appendDelta: (id: string, chunk: string) => void;

  /**
   * Commit a complete, non-text payload atomically — `data-artifact-snapshot`.
   * Unlike `appendDelta`, this replaces rather than accumulates: the caller
   * already stringified the whole payload, there is nothing to append to.
   */
  setSnapshotContent: (id: string, content: string) => void;

  /**
   * Replace the in-flight body with a final one — `data-artifact-replace`.
   *
   * Distinct from `appendDelta` (which accumulates) and `setSnapshotContent`
   * (whose payload is pre-stringified JSON). Sent when the server rewrote a
   * body the client has already rendered: a slide deck that generated outside
   * its template's structure and was repaired after the stream. Without it a
   * create stream — which builds `content` purely by appending — would keep
   * showing the unrepaired deck until a reload.
   */
  replaceContent: (id: string, content: string) => void;

  /**
   * Presentation edit: record which slides will change and the final order, so
   * the canvas can keep untouched slides on screen and scope the loader to the
   * changing slide(s). Driven by `data-artifact-slide-plan`.
   */
  beginSlidePlan: (input: { order: string[]; editing: string[] }) => void;

  /**
   * Presentation edit: append an incremental `<section>` chunk for one slide so
   * it renders live. Driven by `data-artifact-slide-delta`.
   */
  appendSlideDelta: (input: { slideId: string; chunk: string }) => void;

  /**
   * Presentation edit: mark a slide's edit complete, replacing any accumulated
   * partial with the final html. Driven by `data-artifact-slide-finish`.
   */
  finishSlide: (input: { slideId: string; html: string }) => void;

  /**
   * Mark the stream complete. For updates, swap the buffered body into
   * visible content atomically so the canvas never blanks mid-rewrite.
   * Also snapshots the pre-swap body into `previousContent` so the canvas
   * can render a diff highlight against the just-completed update.
   */
  finishStream: () => void;

  /**
   * Drop the diff-highlight baseline. Called after the highlight fade
   * window elapses so subsequent edits don't keep diffing against a stale
   * baseline.
   */
  clearPreviousContent: () => void;

  /**
   * Mark the in-flight stream as failed. Clears the pending buffer (so the
   * spinner stops and the previous body stays visible) and records a
   * user-facing message + the failed instruction so the canvas can show an
   * error banner with a retry affordance.
   */
  failStream: (input: { message: string; instructions?: string }) => void;

  /** Dismiss the error banner and return the canvas to an idle state. */
  dismissError: () => void;
}

/**
 * The artifact store. Standalone — does not register with the global combined
 * store because the canvas is a leaf concern that does not interact with auth,
 * navigation, or workspace state.
 */
export const useArtifactStore = create<ArtifactStore>((set) => ({
  artifact: { ...defaultArtifact },
  focusMode: false,

  setArtifact: (patch) =>
    set((state) => ({ artifact: { ...state.artifact, ...patch } })),

  resetArtifact: () => set({ artifact: { ...defaultArtifact }, focusMode: false }),

  toggleFocusMode: () => set((state) => ({ focusMode: !state.focusMode })),

  setFocusMode: (on) => set({ focusMode: on }),

  showArtifact: (boundingBox) =>
    set((state) => ({
      artifact: {
        ...state.artifact,
        isVisible: true,
        boundingBox: boundingBox ?? centerOfViewport(),
      },
    })),

  hideArtifact: () =>
    set((state) => ({
      artifact: { ...state.artifact, isVisible: false },
      // Closing the canvas always exits focus mode so the sidebar returns.
      focusMode: false,
    })),

  beginCreate: ({ id, title, kind }) =>
    set((state) => ({
      artifact: {
        ...state.artifact,
        id,
        title,
        kind,
        content: "",
        // All kinds stream deltas straight into `content` so the user sees
        // progressive output as the model generates. HTML/presentation
        // canvases handle the "incomplete content looks weird in an iframe"
        // problem locally by defaulting to a source view during streaming
        // and switching to the rendered preview on finish.
        pendingContent: null,
        pendingSlides: null,
        status: "streaming",
        error: null,
        isVisible: true,
        boundingBox:
          state.artifact.isVisible && state.artifact.boundingBox.width > 0
            ? state.artifact.boundingBox
            : centerOfViewport(),
      },
    })),

  beginUpdate: ({ id, title, kind }) =>
    set((state) => ({
      artifact: {
        ...state.artifact,
        id,
        title,
        kind,
        // Keep current content visible; buffer the new body invisibly.
        // Empty string (not null) signals "update in flight".
        pendingContent: "",
        // Cleared until the server announces the slide plan; presentation
        // updates then render per-slide from here.
        pendingSlides: null,
        status: "streaming",
        error: null,
        // Auto-open the canvas so users see the rewrite happen, even when
        // the update was triggered from a chat message after the canvas
        // had been closed. Matches Gemini canvas behavior.
        isVisible: true,
        boundingBox:
          state.artifact.isVisible && state.artifact.boundingBox.width > 0
            ? state.artifact.boundingBox
            : centerOfViewport(),
      },
    })),

  appendDelta: (id, chunk) =>
    set((state) => {
      // Drop stragglers for an artifact that is no longer the active one —
      // e.g. late deltas from a second artifact generating in the same turn
      // must not mix into the currently-open artifact's content. Empty id
      // (legacy/unknown) falls through to preserve old behavior.
      if (id && state.artifact.id !== id) return {};
      const isUpdate = state.artifact.pendingContent !== null;
      if (isUpdate) {
        return {
          artifact: {
            ...state.artifact,
            pendingContent: (state.artifact.pendingContent ?? "") + chunk,
          },
        };
      }
      return {
        artifact: {
          ...state.artifact,
          content: state.artifact.content + chunk,
        },
      };
    }),

  setSnapshotContent: (id, content) =>
    set((state) => {
      // Same stragglers-guard as appendDelta. No pendingContent branch: every
      // snapshot kind (image today) is create-only — update_artifact refuses
      // them — so there is no in-flight update body to buffer into instead.
      if (id && state.artifact.id !== id) return {};
      return {
        artifact: {
          ...state.artifact,
          content,
        },
      };
    }),

  replaceContent: (id, content) =>
    set((state) => {
      // Same stragglers-guard as appendDelta.
      if (id && state.artifact.id !== id) return {};
      // Mirrors appendDelta's branch: during an update the visible body is the
      // OLD deck and the new one is buffered until finish, so a replacement
      // has to land in the buffer or it would swap the canvas early and then
      // be overwritten by the buffered body on finish.
      if (state.artifact.pendingContent !== null) {
        return { artifact: { ...state.artifact, pendingContent: content } };
      }
      return {
        artifact: {
          ...state.artifact,
          content,
          // The per-slide overlay describes the stream that just ended; keeping
          // it would leave the repaired deck rendering through a stale plan.
          pendingSlides: null,
        },
      };
    }),

  beginSlidePlan: ({ order, editing }) =>
    set((state) => ({
      artifact: {
        ...state.artifact,
        pendingSlides: { order, editing, byId: {} },
      },
    })),

  appendSlideDelta: ({ slideId, chunk }) =>
    set((state) => {
      const current = state.artifact.pendingSlides;
      // A delta can only arrive after a plan; ignore stray ones defensively.
      if (!current) return { artifact: state.artifact };
      const prev = current.byId[slideId];
      // Once a slide has finished, ignore late deltas so we don't reopen it.
      if (prev?.done) return { artifact: state.artifact };
      return {
        artifact: {
          ...state.artifact,
          pendingSlides: {
            ...current,
            byId: {
              ...current.byId,
              [slideId]: { html: (prev?.html ?? "") + chunk, done: false },
            },
          },
        },
      };
    }),

  finishSlide: ({ slideId, html }) =>
    set((state) => {
      const current = state.artifact.pendingSlides;
      if (!current) return { artifact: state.artifact };
      return {
        artifact: {
          ...state.artifact,
          pendingSlides: {
            ...current,
            byId: { ...current.byId, [slideId]: { html, done: true } },
          },
        },
      };
    }),

  finishStream: () =>
    set((state) => {
      const pending = state.artifact.pendingContent;
      if (typeof pending === "string" && pending.length > 0) {
        // Update stream completed — snapshot the previous body so the
        // canvas can diff-highlight against it. Skipped when previous
        // content equals the new body (no real change to mark).
        const previousContent =
          state.artifact.content && state.artifact.content !== pending
            ? state.artifact.content
            : null;
        return {
          artifact: {
            ...state.artifact,
            previousContent,
            content: pending,
            pendingContent: null,
            pendingSlides: null,
            status: "idle",
            error: null,
          },
        };
      }
      return {
        artifact: {
          ...state.artifact,
          pendingContent: null,
          pendingSlides: null,
          status: "idle",
          error: null,
        },
      };
    }),

  clearPreviousContent: () =>
    set((state) => ({
      artifact: { ...state.artifact, previousContent: null },
    })),

  failStream: ({ message, instructions }) =>
    set((state) => ({
      artifact: {
        ...state.artifact,
        // Drop the buffer so the spinner stops; `content` is untouched for
        // updates (old body stays) and holds partial text for creates.
        pendingContent: null,
        pendingSlides: null,
        previousContent: null,
        status: "error",
        error: message,
        lastInstruction: instructions ?? state.artifact.lastInstruction,
      },
    })),

  dismissError: () =>
    set((state) =>
      state.artifact.status === "error"
        ? { artifact: { ...state.artifact, status: "idle", error: null } }
        : { artifact: state.artifact },
    ),
}));

/**
 * Returns a small bounding box at the viewport center.
 *
 * Used as the open-animation origin when the canvas is shown without a
 * specific trigger element (e.g. an artifact stream auto-opens it).
 */
function centerOfViewport(): UIArtifact["boundingBox"] {
  if (typeof window === "undefined") {
    return { top: 0, left: 0, width: 100, height: 40 };
  }
  return {
    top: window.innerHeight / 2 - 20,
    left: window.innerWidth / 2 - 50,
    width: 100,
    height: 40,
  };
}
